import {
  ArrowForwardIosOutlined,
  CategoryOutlined,
  CheckCircleOutlined,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  InventoryOutlined,
  LayersOutlined,
  MonetizationOnOutlined,
  MoreVertRounded,
  RefreshRounded,
  TrendingDown,
  TrendingUp,
  ViewListOutlined,
  WarningAmberOutlined,
  WorkOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import type {
  IBrandModel,
  IEquipment,
  PaginatedResponse,
} from "../../../types/equipment";

interface GearSetItem {
  id: number;
  brand_model: number | null;
  quantity?: number;
}

interface GearSet {
  id: number;
  name: string;
  description?: string;
  status: string;
  items_count: number;
  items?: GearSetItem[];
}

interface Rental {
  id: number;
  equipment: number;
  equipment_name?: string
  status: string;
  rental_start_date: string;
  rental_end_date: string;
  daily_rate?: string;
}

interface Stream {
  id: number;
  title?: string;
  date?: string;
  venue?: { name?: string };
}

interface ActivityLog {
  id: number;
  action: string;
  table_name: string;
  additional_info?: string;
  created_at: string;
  user?: number;
  user_email?: string;
}

const EnhancedMetricCard: React.FC<{
  title: string;
  value: number;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
  trend?: number;
  onClick?: () => void;
}> = ({ title, value, icon: Icon, color, subtitle, trend, onClick }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1200;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value]);

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2,
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: "1px solid",
        borderColor: `${color}30`,
        position: "relative",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: onClick ? "translateY(-4px)" : "none",
          boxShadow: onClick ? `0 8px 16px ${color}25` : "none",
          borderColor: onClick ? color : `${color}30`,
          "& .metric-icon": { transform: "rotate(10deg) scale(1.1)" },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: -30,
          right: -30,
          width: "100px",
          height: "100px",
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
          borderRadius: "50%",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box
          className="metric-icon"
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 8px ${color}40`,
            transition: "transform 0.4s ease",
          }}
        >
          <Icon sx={{ color: "white", fontSize: 24 }} />
        </Box>
        {trend !== undefined && (
          <Chip
            icon={trend >= 0 ? <TrendingUp /> : <TrendingDown />}
            label={`${trend >= 0 ? "+" : ""}${trend}%`}
            size="small"
            sx={{
              backgroundColor: trend >= 0 ? "#10b98125" : "#ef444425",
              color: trend >= 0 ? "#10b981" : "#ef4444",
              fontWeight: 700,
              fontSize: "0.65rem",
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        )}
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          mb: 0.5,
          color,
          fontSize: "2rem",
          lineHeight: 1,
          letterSpacing: "-0.02em",
        }}
      >
        {displayValue.toLocaleString()}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          mb: subtitle ? 0.5 : 0,
          fontSize: "0.75rem",
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", fontWeight: 500, fontSize: "0.65rem" }}
        >
          {subtitle}
        </Typography>
      )}

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${color} 0%, ${color}60 100%)`,
          opacity: 0.6,
        }}
      />
    </Paper>
  );
};

const EnhancedChartWidget: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  height?: string | number;
  onRefresh?: () => void;
}> = ({ title, subtitle, children, actions, height = "100%", onRefresh }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    onRefresh?.();
    setTimeout(() => setRefreshing(false), 1000);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height,
        p: 2,
        borderRadius: 2,
        backgroundColor: colors.primary[900],
        border: "1px solid",
        borderColor: "grey.200",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
          borderColor: "primary.main",
          "& .chart-actions": { opacity: 1 },
        },
      }}
    >
      {refreshing && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            zIndex: 10,
          }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "text.primary",
              mb: subtitle ? 0.5 : 0,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.7rem",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          className="chart-actions"
          sx={{
            display: "flex",
            gap: 0.5,
            opacity: 0,
            transition: "opacity 0.3s",
          }}
        >
          {actions}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="More">
            <IconButton size="small">
              <MoreVertRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {children}
    </Paper>
  );
};

const QuickNavCard: React.FC<{
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  onClick: () => void;
  badge?: string | number;
}> = ({ label, description, icon: Icon, color, onClick, badge }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2.5,
      borderRadius: 2,
      cursor: "pointer",
      background: `linear-gradient(135deg, ${color}12 0%, ${color}05 100%)`,
      border: "1px solid",
      borderColor: `${color}25`,
      display: "flex",
      alignItems: "center",
      gap: 2,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: `0 8px 20px ${color}20`,
        borderColor: color,
        "& .nav-arrow": { transform: "translateX(4px)" },
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: `0 4px 10px ${color}35`,
      }}
    >
      <Icon sx={{ color: "white", fontSize: 22 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}
        >
          {label}
        </Typography>
        {badge !== undefined && (
          <Chip
            label={badge}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              backgroundColor: `${color}20`,
              color,
              borderRadius: "6px",
            }}
          />
        )}
      </Box>
      <Typography
        sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}
        noWrap
      >
        {description}
      </Typography>
    </Box>
    <ArrowForwardIosOutlined
      className="nav-arrow"
      sx={{
        fontSize: 13,
        color: "text.disabled",
        transition: "transform 0.2s",
        flexShrink: 0,
      }}
    />
  </Paper>
);

const fmtDate = (d?: string) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const timeAgo = (d?: string) => {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${mins}m ago`;
};

const ACTION_COLOR: Record<string, string> = {
  CREATE: "#10b981",
  UPDATE: "#4285f4",
  DELETE: "#ef4444",
  VIEW: "#94a3b8",
  ALLOCATE: "#f59e0b",
};

const ACTIVITY_PER_PAGE = 5;

const GearManagementDashboard: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [equipment, setEquipment] = useState<IEquipment[]>([]);
  const [brandModels, setBrandModels] = useState<IBrandModel[]>([]);
  const [gearSets, setGearSets] = useState<GearSet[]>([]);
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [activity, setActivity] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityPage, setActivityPage] = useState(0);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [eqRes, bmRes, gsRes, rentRes, streamRes] =
        await Promise.allSettled([
          apiService.get<PaginatedResponse<IEquipment>>(
            "/api/equipments/equipment/?page_size=1000",
          ),
          apiService.get<PaginatedResponse<IBrandModel>>(
            "/api/equipments/brand-model/",
          ),
          apiService.get<PaginatedResponse<GearSet>>(
            "/api/equipments/gearsets/",
          ),
          apiService.get<PaginatedResponse<Rental>>(
            "/api/equipments/equipment-rentals/?status=active&page_size=20",
          ),
          apiService.get<PaginatedResponse<Stream>>(
            "/api/streams/stream/?page_size=10",
          ),
        ]);

      if (eqRes.status === "fulfilled")
        setEquipment(eqRes.value.data.results || []);
      if (bmRes.status === "fulfilled")
        setBrandModels(bmRes.value.data.results || []);
      if (gsRes.status === "fulfilled")
        setGearSets(gsRes.value.data.results || []);
      if (rentRes.status === "fulfilled")
        setRentals(rentRes.value.data.results || []);
      if (streamRes.status === "fulfilled")
        setStreams(streamRes.value.data.results || []);

      try {
        const actRes = await apiService.get<PaginatedResponse<ActivityLog>>(
          "/api/logs/system-log/?page_size=30&table_name=equipment",
        );
        setActivity(actRes.data.results || []);
      } catch (e) {
        console.error(e);
      }
    } catch {
      setError("Failed to load dashboard data.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const total = equipment.length;
  const available = equipment.filter((e) => e.status === "available").length;
  const outOnJob = equipment.filter((e) =>
    ["allocated", "rented"].includes(e.status),
  ).length;
  const maintenance = equipment.filter(
    (e) => e.status === "maintenance",
  ).length;
  const lost = equipment.filter((e) => e.status === "lost").length;
  const overdueRentals = rentals.filter(
    (r) => new Date(r.rental_end_date) < new Date(),
  ).length;
  const needsAttention = overdueRentals + lost;

  const categoryMap: Record<string, number> = {};
  brandModels.forEach((bm) => {
    const cat =
      bm.category && typeof bm.category === "object"
        ? (bm.category as { name: string }).name
        : "Uncategorized";
    const qty = bm.brand_model_quantity ?? 0;
    categoryMap[cat] = (categoryMap[cat] || 0) + qty;
  });

  const PIE_COLORS = [
    "#4285f4",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#64748b",
  ];
  const categoryPieData = Object.entries(categoryMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([label, value], id) => ({ id, label, value, color: PIE_COLORS[id] }));

  const statusBarData = [
    { status: "Available", count: available, color: "#10b981" },
    { status: "Out on Job", count: outOnJob, color: "#4285f4" },
    { status: "Maintenance", count: maintenance, color: "#f59e0b" },
    { status: "Lost", count: lost, color: "#ef4444" },
  ];

  // Gear sets with shortages
  const setsWithShortage = gearSets.filter((gs) =>
    (gs.items || []).some((item) => {
      const bm = brandModels.find((b) => b.id === item.brand_model);
      return (bm?.available_quantity ?? 0) < (item.quantity || 1);
    }),
  );

  const upcomingStreams = streams
    .filter((s) => s.date && new Date(s.date) >= new Date())
    .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime())
    .slice(0, 4);

  const pagedActivity = activity.slice(
    activityPage * ACTIVITY_PER_PAGE,
    (activityPage + 1) * ACTIVITY_PER_PAGE,
  );
  const activityTotalPages = Math.ceil(activity.length / ACTIVITY_PER_PAGE);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            fontWeight={800}
            color="text.primary"
            lineHeight={1}
          >
            Gear Management
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {setsWithShortage.length > 0 && (
          <Box
            onClick={() => navigate("/gear-management/sets")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              backgroundColor: "#f59e0b12",
              border: "1px solid #f59e0b44",
              borderRadius: 2,
              px: 2.5,
              py: 1.5,
              mb: 3,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f59e0b20" },
            }}
          >
            <WarningAmberOutlined sx={{ fontSize: 18, color: "#f59e0b" }} />
            <Typography
              sx={{ fontSize: 13, color: "#f59e0b", fontWeight: 600 }}
            >
              {setsWithShortage.length} gear set
              {setsWithShortage.length > 1 ? "s have" : " has"} stock shortages
              -
              <span style={{ textDecoration: "underline", marginLeft: 4 }}>
                view gear sets
              </span>
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <EnhancedMetricCard
              title="Total Gear"
              value={total}
              icon={InventoryOutlined}
              color="#4285f4"
              subtitle="Across all categories"
              onClick={() => navigate("/gear-management/list")}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <EnhancedMetricCard
              title="Available"
              value={available}
              icon={CheckCircleOutlined}
              color="#10b981"
              subtitle="Ready for deployment"
              onClick={() => navigate("/gear-management/list")}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <EnhancedMetricCard
              title="Out on Job"
              value={outOnJob}
              icon={WorkOutlined}
              color="#f59e0b"
              subtitle="Allocated or rented"
              onClick={() => navigate("/gear-management/list")}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <EnhancedMetricCard
              title="Needs Attention"
              value={needsAttention}
              icon={WarningAmberOutlined}
              color="#ef4444"
              subtitle="Overdue or lost"
              onClick={() => navigate("/gear-management/list")}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                mb: 1.5,
                color: "text.primary",
              }}
            >
              Quick Access
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <QuickNavCard
                  label="All Gear"
                  description="Browse and manage every physical unit"
                  icon={ViewListOutlined}
                  color="#4285f4"
                  badge={total}
                  onClick={() => navigate("/gear-management/list")}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <QuickNavCard
                  label="Gear Sets"
                  description="Manage reusable equipment templates"
                  icon={LayersOutlined}
                  color="#8b5cf6"
                  badge={gearSets.length}
                  onClick={() => navigate("/gear-management/sets")}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <QuickNavCard
                  label="Catalog"
                  description="Browse equipment by brand & model"
                  icon={CategoryOutlined}
                  color="#10b981"
                  badge={brandModels.length}
                  onClick={() => navigate("/gear-management/catalog")}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <QuickNavCard
                  label="Active Rentals"
                  description="Track gear currently out on rental"
                  icon={MonetizationOnOutlined}
                  color="#f59e0b"
                  badge={rentals.length}
                  onClick={() => {}}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, lg: 8 }}>
            <EnhancedChartWidget
              title="Inventory Status Breakdown"
              subtitle="Current distribution of all gear"
              onRefresh={fetchAll}
            >
              <BarChart
                xAxis={[
                  {
                    scaleType: "band",
                    data: statusBarData.map((s) => s.status),
                    tickLabelStyle: { fontSize: 12, fill: "#666" },
                  },
                ]}
                yAxis={[{ tickLabelStyle: { fontSize: 11, fill: "#666" } }]}
                series={[
                  {
                    data: statusBarData.map((s) => s.count),
                    label: "Units",
                    color: "#4285f4",
                  },
                ]}
                height={280}
                margin={{ top: 20, bottom: 40, left: 50, right: 20 }}
                grid={{ horizontal: true }}
                sx={{
                  "& .MuiBarElement-root": { rx: 6 },
                  "& .MuiChartsGrid-line": {
                    stroke: "rgba(0,0,0,0.08)",
                    strokeDasharray: "3 3",
                  },
                }}
              />
            </EnhancedChartWidget>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <EnhancedChartWidget
              title="Categories"
              subtitle="Units by equipment category"
              height="100%"
              onRefresh={fetchAll}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <PieChart
                  series={[
                    {
                      data: categoryPieData,
                      innerRadius: 50,
                      outerRadius: 90,
                      paddingAngle: 2,
                      cornerRadius: 4,
                      highlightScope: { fade: "global", highlight: "item" },
                      faded: {
                        innerRadius: 20,
                        additionalRadius: -20,
                        color: "gray",
                      },
                    },
                  ]}
                  height={200}
                  margin={{ top: 15, bottom: 15, left: 15, right: 15 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "center",
                  }}
                >
                  {categoryPieData.map((cat) => (
                    <Chip
                      key={cat.id}
                      label={`${cat.label}: ${cat.value}`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        backgroundColor: `${cat.color}15`,
                        border: "1px solid",
                        borderColor: `${cat.color}30`,
                        fontSize: "0.7rem",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </EnhancedChartWidget>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <EnhancedChartWidget
              title="Gear Sets"
              subtitle="Reusable equipment templates"
              onRefresh={fetchAll}
              actions={
                <Tooltip title="View all">
                  <IconButton
                    size="small"
                    onClick={() => navigate("/gear-management/sets")}
                  >
                    <ArrowForwardIosOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              {gearSets.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    No gear sets yet.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {gearSets.slice(0, 5).map((gs) => {
                    const shortage = setsWithShortage.some(
                      (s) => s.id === gs.id,
                    );
                    return (
                      <Box
                        key={gs.id}
                        onClick={() =>
                          navigate(`/gear-management/sets/${gs.id}`)
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1.5,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          backgroundColor: `${colors.primary[800]}`,
                          border: "1px solid",
                          borderColor: shortage ? "#f59e0b33" : "transparent",
                          transition: "all 0.2s",
                          "&:hover": {
                            borderColor: "#8b5cf680",
                            // backgroundColor: `${colors.primary[700]}`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            backgroundColor: "#8b5cf618",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <LayersOutlined
                            sx={{ fontSize: 18, color: "#8b5cf6" }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                              noWrap
                            >
                              {gs.name}
                            </Typography>
                            {shortage && (
                              <WarningAmberOutlined
                                sx={{
                                  fontSize: 13,
                                  color: "#f59e0b",
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            sx={{ fontSize: 11, color: "text.secondary" }}
                          >
                            {gs.items_count} model type
                            {gs.items_count !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                        <Chip
                          label={gs.status}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 600,
                            textTransform: "capitalize",
                            backgroundColor: "#10b98115",
                            color: "#10b981",
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </EnhancedChartWidget>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <EnhancedChartWidget
              title="Upcoming Events"
              subtitle="Streams requiring gear in the near future"
              onRefresh={fetchAll}
            >
              {upcomingStreams.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    No upcoming events.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {upcomingStreams.map((s) => {
                    const daysUntil = Math.ceil(
                      (new Date(s.date!).getTime() - Date.now()) / 86_400_000,
                    );
                    const urgent = daysUntil <= 3;
                    return (
                      <Box
                        key={s.id}
                        onClick={() =>
                          navigate(`/stream-management/view/${s.id}`)
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1.5,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          backgroundColor: colors.primary[800],
                          border: "1px solid",
                          borderColor: urgent ? "#ef444433" : "transparent",
                          transition: "all 0.2s",
                          "&:hover": { borderColor: "#4285f480" },
                        }}
                      >
                        <Box
                          sx={{
                            textAlign: "center",
                            minWidth: 40,
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 20,
                              fontWeight: 800,
                              color: urgent ? "#ef4444" : "#4285f4",
                              lineHeight: 1,
                            }}
                          >
                            {new Date(s.date!).getDate()}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 9,
                              color: "text.secondary",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            {new Date(s.date!).toLocaleDateString(undefined, {
                              month: "short",
                            })}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                            noWrap
                          >
                            {s.title || `Stream #${s.id}`}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 11, color: "text.secondary" }}
                            noWrap
                          >
                            {s.venue?.name || "—"} · in {daysUntil} day
                            {daysUntil !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                        {urgent && (
                          <Chip
                            label="Soon"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 700,
                              backgroundColor: "#ef444422",
                              color: "#ef4444",
                              borderRadius: 1,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </EnhancedChartWidget>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <EnhancedChartWidget
              title="Active Rentals"
              subtitle={`${rentals.length} rental${rentals.length !== 1 ? "s" : ""} currently out`}
              onRefresh={fetchAll}
            >
              {rentals.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    No active rentals.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {rentals.slice(0, 5).map((r) => {
                    const overdue = new Date(r.rental_end_date) < new Date();
                    return (
                      <Box
                        key={r.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1.5,
                          borderRadius: 1.5,
                          backgroundColor: colors.primary[800],
                          border: "1px solid",
                          borderColor: overdue ? "#ef444433" : "transparent",
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            backgroundColor: overdue
                              ? "#ef444418"
                              : "#f59e0b18",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <MonetizationOnOutlined
                            sx={{
                              fontSize: 18,
                              color: overdue ? "#ef4444" : "#f59e0b",
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                            noWrap
                          >
                            {r.equipment_name || `Rental #${r.id}`}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 11, color: "text.secondary" }}
                            >
                              Due {fmtDate(r.rental_end_date)}
                            </Typography>
                            {overdue && (
                              <Chip
                                label="Overdue"
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  backgroundColor: "#ef444422",
                                  color: "#ef4444",
                                  borderRadius: 1,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                        {r.daily_rate && (
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#10b981",
                              flexShrink: 0,
                            }}
                          >
                            ${r.daily_rate}/d
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </EnhancedChartWidget>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <EnhancedChartWidget
              title="Recent Activity"
              subtitle="Latest equipment-related actions"
              onRefresh={fetchAll}
            >
              {activity.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    No recent activity.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {pagedActivity.map((log) => {
                      const color = ACTION_COLOR[log.action] ?? "#94a3b8";
                      return (
                        <Box
                          key={log.id}
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: 1,
                              backgroundColor: `${color}18`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 9,
                                fontWeight: 800,
                                color,
                                textTransform: "uppercase",
                                letterSpacing: 0.3,
                              }}
                            >
                              {log.action.slice(0, 3)}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "text.primary",
                                fontWeight: 500,
                              }}
                              noWrap
                            >
                              {log.additional_info ||
                                `${log.action} on ${log.table_name}`}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 11, color: "text.secondary" }}
                            >
                              {log.user_email || "System"} ·{" "}
                              {timeAgo(log.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {activityTotalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 2,
                      }}
                    >
                      <IconButton
                        size="small"
                        disabled={activityPage === 0}
                        onClick={() => setActivityPage((p) => p - 1)}
                        sx={{
                          width: 28,
                          height: 28,
                          "&:disabled": { opacity: 0.3 },
                        }}
                      >
                        <ChevronLeftOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                      <Typography
                        sx={{ fontSize: 11, color: "text.secondary", px: 0.5 }}
                      >
                        {activityPage + 1} / {activityTotalPages}
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={activityPage >= activityTotalPages - 1}
                        onClick={() => setActivityPage((p) => p + 1)}
                        sx={{
                          width: 28,
                          height: 28,
                          "&:disabled": { opacity: 0.3 },
                        }}
                      >
                        <ChevronRightOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            </EnhancedChartWidget>
          </Grid>

          {/* <Grid size={{ xs: 12, lg: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2, borderRadius: 2, height: "100%",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white", position: "relative", overflow: "hidden",
                "&::before": {
                  content: '""', position: "absolute", top: -80, right: -80,
                  width: "200px", height: "200px",
                  background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
                  borderRadius: "50%",
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: "1rem" }}>
                Inventory Health
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.75, fontSize: "0.75rem" }}>Availability Rate</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: "2rem" }}>
                    {total > 0 ? ((available / total) * 100).toFixed(1) : 0}%
                  </Typography>
                  <LinearProgress variant="determinate" value={total > 0 ? (available / total) * 100 : 0}
                    sx={{ mt: 1.5, height: 6, borderRadius: 3, backgroundColor: "rgba(255,255,255,0.2)", "& .MuiLinearProgress-bar": { borderRadius: 3, backgroundColor: "white" } }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.75, fontSize: "0.75rem" }}>Utilisation Rate</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: "2rem" }}>
                    {total > 0 ? ((outOnJob / total) * 100).toFixed(1) : 0}%
                  </Typography>
                  <LinearProgress variant="determinate" value={total > 0 ? (outOnJob / total) * 100 : 0}
                    sx={{ mt: 1.5, height: 6, borderRadius: 3, backgroundColor: "rgba(255,255,255,0.2)", "& .MuiLinearProgress-bar": { borderRadius: 3, backgroundColor: "white" } }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.75, fontSize: "0.75rem" }}>Gear Sets Ready</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: "2rem" }}>
                    {gearSets.length - setsWithShortage.length} / {gearSets.length}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={gearSets.length > 0 ? ((gearSets.length - setsWithShortage.length) / gearSets.length) * 100 : 100}
                    sx={{ mt: 1.5, height: 6, borderRadius: 3, backgroundColor: "rgba(255,255,255,0.2)", "& .MuiLinearProgress-bar": { borderRadius: 3, backgroundColor: "white" } }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid> */}
        </Grid>
      </motion.div>
    </Box>
  );
};

export default GearManagementDashboard;
